import { useEffect, useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import { CustomerContact, CustomerContactKind, DossierTemplateKind, EvidenceDraftSummary } from "../../types/evidence";

// Each choice prints as a Facts bullet and opens Customer Communication; "other" is the agent's own sentence.
type ContactChoice = "none" | CustomerContactKind;
const CONTACT_CHOICES: { value: ContactChoice; label: string }[] = [
  { value: "none", label: "Never contacted us — support desk checked" },
  { value: "after_renewal", label: "Contacted us only after the renewal order" },
  { value: "delivery_status", label: "Contacted us to check on the delivery" },
  { value: "other", label: "Other" },
];

export interface ContactFields {
  noContactAttested: boolean;
  customerContact: CustomerContact | null;
}

// A save that failed left the server holding the old value. The field goes back to it and says so
// here, beside the control, rather than behind a toast that is gone in four seconds.
const CONTACT_SAVE_FAILED = "Not saved — this is the answer the server holds. Choose it again.";

function contactChoice(draft: EvidenceDraftSummary): ContactChoice | "" {
  if (draft.no_contact_attested) return "none";
  return draft.input_overrides?.customerContact?.kind ?? "";
}

export default function CustomerContactField({ draft, template, disabled, onSave, onUnsavedChange }: {
  draft: EvidenceDraftSummary;
  /** A digital draft offers no delivery answer; an answer already given still shows. */
  template: DossierTemplateKind;
  disabled: boolean;
  /** Resolves false when the server did not take the value. */
  onSave: (fields: ContactFields) => Promise<boolean>;
  /** The Generate button sits in another card, so the gate it needs is reported upward. */
  onUnsavedChange: (unsaved: boolean) => void;
}) {
  const saved = contactChoice(draft);
  const savedNote = draft.input_overrides?.customerContact?.note ?? "";
  // "Other" is chosen before its sentence exists, so the choice lives here until the note is typed.
  const [choice, setChoice] = useState<ContactChoice | "">(saved);
  const [note, setNote] = useState(savedNote);
  const [failed, setFailed] = useState(false);
  // A pending "Other" survives the save that clears the previous statement.
  useEffect(() => { if (saved) setChoice(saved); }, [saved]);
  useEffect(() => { setNote(savedNote); }, [savedNote]);
  const save = async (fields: ContactFields) => {
    setFailed(false);
    const stored = await onSave(fields);
    if (stored) return;
    setChoice(saved);
    setNote(savedNote);
    setFailed(true);
  };
  const choose = (value: ContactChoice) => {
    setChoice(value);
    setFailed(false);
    if (value === "none") save({ noContactAttested: true, customerContact: null });
    else if (value === "other") { if (saved) save({ noContactAttested: false, customerContact: null }); }
    else save({ noContactAttested: false, customerContact: { kind: value } });
  };
  // A typed sentence is saved by the blur that clicking Generate causes, like the ARN. "Other" with no
  // sentence gives that blur nothing to save and would print nothing, so it and a save the server
  // refused are what close Generate.
  const unsaved = (choice === "other" && !note.trim()) || failed;
  // Gone from the page, the field holds nothing back from Generate.
  useEffect(() => {
    onUnsavedChange(unsaved);
    return () => onUnsavedChange(false);
  }, [unsaved, onUnsavedChange]);
  // An emptied sentence unsets the contact rather than leaving the old one printed.
  const saveNote = () => {
    const value = note.trim();
    if (value === savedNote) return;
    save({ noContactAttested: false, customerContact: value ? { kind: "other", note: value } : null });
  };

  return (
    <>
      <TextField
        select
        label="Customer contact"
        size="small"
        fullWidth
        value={choice}
        onChange={(event) => choose(event.target.value as ContactChoice)}
        disabled={disabled}
        error={failed}
        helperText={failed ? CONTACT_SAVE_FAILED : undefined}
        sx={{ mt: 1 }}
      >
        {CONTACT_CHOICES.filter((option) => option.value === choice || !(template === "digital" && option.value === "delivery_status")).map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </TextField>
      {choice === "other" ? (
        <TextField
          label="Contact sentence"
          size="small"
          fullWidth
          value={note}
          onChange={(event) => { setNote(event.target.value); setFailed(false); }}
          onBlur={saveNote}
          disabled={disabled}
          slotProps={{ htmlInput: { maxLength: 300 } }}
          sx={{ mt: 1 }}
        />
      ) : null}
    </>
  );
}
